import React from 'react';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import { BET_ALREADY_MESSAGE, getAnswerColor, getBetDisplayLabel } from '../betLabels';
import { calculateCancelPreview } from '../betCancelPreview';

function BetChoiceCardPC({
  choice,
  displayLabel,
  colorSet: providedColorSet,
  index,
  rate = '-',
  betPoint,
  betDetail = null,
  pendingPoint = 0,
  betValues,
  displayPoint,
  maxBet,
  canBetTime,
  isHighlighted,
  disabledBySideLock,
  sideLockMessage = BET_ALREADY_MESSAGE,
  betEntryStatus = 'notBet',
  onBet,
  onConfirmBet,
  onCancelBet,
  onCancelConfirmedBet,
  isBetSubmitting = false,
  isBetCancelling = false,
  canCancelConfirmedBet = false,
  compact = false,
  choiceColumnCount = 2,
}) {
  const colorSet = providedColorSet || getAnswerColor(index);
  const label = displayLabel || getBetDisplayLabel(index);
  const currentBetPoint = Number(betPoint || 0);
  const currentPendingPoint = Number(pendingPoint || 0);
  const availablePoint = Math.max(Number(displayPoint || 0) - currentPendingPoint, 0);
  const isSelected = currentBetPoint > 0 || currentPendingPoint > 0;
  const currentBetRate = betDetail?.rate ?? betDetail?.option_rate ?? null;
  const numericCurrentBetRate = Number(currentBetRate);
  const numericLiveRate = Number(rate);
  const displayBetRate = Number.isFinite(numericCurrentBetRate) ? numericCurrentBetRate.toFixed(2) : rate;
  const expectedPayout = betDetail?.expected_payout ?? null;
  const { refundPoint: cancelRefundPoint } = calculateCancelPreview(currentBetPoint);
  const isBusy = isBetSubmitting || isBetCancelling;
  const isBetPlaced = betEntryStatus === 'placed';
  const canShowBetControls = !isBetPlaced;
  const canCommitPending = currentPendingPoint > 0 && canBetTime && !disabledBySideLock && !isBusy;
  const canCancelPending = currentPendingPoint > 0 && !isBusy;
  const showRateUpCue =
    canCancelConfirmedBet &&
    currentBetPoint > 0 &&
    Number.isFinite(numericCurrentBetRate) &&
    Number.isFinite(numericLiveRate) &&
    numericLiveRate > numericCurrentBetRate;
  const actionButtonFontSize = choiceColumnCount >= 4 ? '11px' : choiceColumnCount >= 3 ? '12px' : '13px';
  const actionButtonPadding = choiceColumnCount >= 4 ? '5px 4px' : '6px 8px';
  const betActionButtonSx = {
    fontSize: actionButtonFontSize,
    padding: actionButtonPadding,
    minWidth: 0,
    height: compact ? '36px' : '34px',
    backgroundColor: colorSet.primary,
    color: '#fff',
    boxShadow: 'none',
    whiteSpace: 'nowrap',
    lineHeight: 1.15,
    '&:hover': {
      backgroundColor: colorSet.hover,
      boxShadow: '0 3px 6px rgba(0,0,0,0.15)',
    },
  };
  const cancelPendingButtonSx = {
    fontSize: actionButtonFontSize,
    padding: actionButtonPadding,
    minWidth: 0,
    height: compact ? '36px' : '34px',
    color: colorSet.primary,
    borderColor: colorSet.primary,
    whiteSpace: 'nowrap',
    lineHeight: 1.15,
    '&:hover': {
      borderColor: colorSet.hover,
      backgroundColor: 'rgba(255,255,255,0.55)',
    },
  };
  const cardPadding = compact ? 1.5 : 1;
  const contentGap = compact ? 1 : 0.5;
  const choiceMinHeight = compact ? '36px' : '32px';

  const isBetButtonDisabled = (value) =>
    disabledBySideLock ||
    value > availablePoint ||
    maxBet < value + currentPendingPoint ||
    !canBetTime ||
    isBusy;

  const cancelConfirmedButtonSx = {
    fontSize: '13px',
    fontWeight: 900,
    padding: '6px 10px',
    minWidth: 0,
    height: '48px',
    color: showRateUpCue ? '#d84315' : '#37474f',
    borderColor: showRateUpCue ? '#ff8a2d' : 'rgba(55, 71, 79, 0.58)',
    backgroundColor: showRateUpCue ? '#fff8e1' : 'rgba(96, 125, 139, 0.08)',
    boxShadow: showRateUpCue ? '0 0 0 2px rgba(255, 138, 45, 0.18)' : 'none',
    textTransform: 'none',
    transition: 'border-color 0.2s ease, background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      borderColor: showRateUpCue ? '#f4511e' : '#263238',
      backgroundColor: showRateUpCue ? '#fff3cd' : 'rgba(96, 125, 139, 0.16)',
      boxShadow: showRateUpCue ? '0 0 0 2px rgba(255, 138, 45, 0.24)' : 'none',
    },
  };

  return (
    <Box
      sx={{
        position: 'relative',
        mb: compact ? 1 : 0.5,
        fontSize: '13px',
        p: cardPadding,
        borderRadius: '14px',
        border: `1px solid ${isHighlighted || isSelected ? colorSet.primary : colorSet.border}`,
        backgroundColor: disabledBySideLock && !isSelected ? 'rgba(0,0,0,0.04)' : colorSet.surface,
        opacity: disabledBySideLock && !isSelected ? 0.58 : 1,
        boxShadow: isHighlighted
          ? `0 0 0 2px ${colorSet.border}, 0 0 18px ${colorSet.border}`
          : isSelected ? `0 0 0 2px ${colorSet.border}` : 'none',
        transition: 'opacity 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
        height: '100%',
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
          mb: compact ? 1 : 0.25,
        }}
      >
        <Box
          component="h3"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.4,
            fontSize: compact ? '15px' : '16px',
            margin: 0,
            color: colorSet.primary,
            lineHeight: 1.35,
            wordBreak: 'break-word',
            minWidth: 0,
          }}
        >
          <Box
            component="span"
            sx={{
              alignSelf: 'flex-start',
              px: 0.8,
              py: 0.2,
              borderRadius: 999,
              fontSize: '11px',
              fontWeight: 900,
              color: colorSet.primary,
              bgcolor: '#fff',
              border: `1px solid ${colorSet.border}`,
              whiteSpace: 'nowrap',
            }}
          >
            {label}
          </Box>
          <Box
            component="span"
            sx={{
              minHeight: choiceMinHeight,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {choice}
          </Box>
        </Box>
        {isSelected && (
          <Box
            component="span"
            sx={{
              flexShrink: 0,
              px: 0.9,
              py: 0.25,
              borderRadius: 999,
              fontSize: '11px',
              fontWeight: 800,
              color: colorSet.primary,
              bgcolor: '#fff',
              border: `1px solid ${colorSet.border}`,
            }}
          >
            選択済み
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: contentGap }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 0.75 }}>
          <Box sx={{ color: colorSet.primary, fontWeight: 700, minWidth: 0 }}>
            <Box sx={{ fontSize: '11px', color: 'rgba(15, 23, 42, 0.58)' }}>ベット予定</Box>
            <Box
              sx={{
                padding: '4px 8px',
                borderRadius: '6px',
                backgroundColor: currentPendingPoint > 0 ? '#fff7ed' : '#fff',
                border: `1px solid ${currentPendingPoint > 0 ? colorSet.primary : colorSet.border}`,
              }}
            >
              {currentPendingPoint}
            </Box>
          </Box>
          <Box sx={{ color: colorSet.primary, fontWeight: 700, minWidth: 0 }}>
            <Box sx={{ fontSize: '11px', color: 'rgba(15, 23, 42, 0.58)' }}>現在のレート</Box>
            <Box>{rate}倍</Box>
          </Box>
        </Box>

        {currentBetPoint > 0 && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gap: 0.75,
              p: 1,
              borderRadius: '8px',
              bgcolor: '#fff',
              border: `1px solid ${colorSet.border}`,
              color: colorSet.primary,
              fontWeight: 800,
            }}
          >
            <Box>
              <Box sx={{ fontSize: '11px', color: 'rgba(15, 23, 42, 0.58)' }}>ベット額</Box>
              <Box>{currentBetPoint} pt</Box>
            </Box>
            <Box>
              <Box sx={{ fontSize: '11px', color: 'rgba(15, 23, 42, 0.58)' }}>決定済レート</Box>
              <Box>{displayBetRate}倍</Box>
            </Box>
            <Box>
              <Box sx={{ fontSize: '11px', color: 'rgba(15, 23, 42, 0.58)' }}>的中時</Box>
              <Box>{expectedPayout ?? '-'} pt</Box>
            </Box>
          </Box>
        )}

        {canCancelConfirmedBet ? (
          <>
            <Box
              sx={{
                minHeight: compact ? '56px' : '60px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.75,
                visibility: showRateUpCue ? 'visible' : 'hidden',
                opacity: showRateUpCue ? 1 : 0,
                transition: 'opacity 0.2s ease',
                pointerEvents: 'none',
              }}
              aria-hidden={!showRateUpCue}
            >
              <Box
                component="img"
                src="/rate-up-popup-pc-candidate.png"
                width={560}
                height={220}
                alt="レートUP中！"
                sx={{ width: compact ? '118px' : '138px', maxWidth: '42%', height: 'auto', flexShrink: 0 }}
              />
              <Box sx={{ fontSize: '11px', fontWeight: 800, color: '#6d4c41', lineHeight: 1.35 }}>
                いま入れ直すと、より高いレートでベットできます
              </Box>
            </Box>
            <Button
              disabled={isBusy}
              variant="outlined"
              onClick={() => onCancelConfirmedBet?.()}
              sx={cancelConfirmedButtonSx}
            >
              <Box component="span" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.15 }}>
                <Box component="span">ベット取消</Box>
                <Box component="span" sx={{ fontSize: '11px', fontWeight: 800 }}>
                  {cancelRefundPoint}pt返却
                </Box>
              </Box>
            </Button>
          </>
        ) : canShowBetControls && (
          <>
            {compact ? (
              <>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 0.75 }}>
                  {betValues.map((value) => (
                    <Button
                      key={value}
                      disabled={isBetButtonDisabled(value)}
                      variant="contained"
                      onClick={() => onBet(index, value)}
                      value={value}
                      sx={betActionButtonSx}
                    >
                      {value}
                    </Button>
                  ))}
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 0.75 }}>
                  <Button
                    disabled={!canCommitPending}
                    variant="contained"
                    onClick={() => onConfirmBet(index)}
                    sx={betActionButtonSx}
                  >
                    決定
                  </Button>
                  <Button
                    disabled={!canCancelPending}
                    variant="outlined"
                    onClick={() => onCancelBet(index)}
                    sx={cancelPendingButtonSx}
                  >
                    キャンセル
                  </Button>
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${betValues.length + 2}, minmax(0, 1fr))`,
                  gap: 0.75,
                }}
              >
                {betValues.map((value) => (
                  <Button
                    key={value}
                    disabled={isBetButtonDisabled(value)}
                    variant="contained"
                    onClick={() => onBet(index, value)}
                    value={value}
                    sx={betActionButtonSx}
                  >
                    {value}
                  </Button>
                ))}
                <Button
                  disabled={!canCommitPending}
                  variant="contained"
                  onClick={() => onConfirmBet(index)}
                  sx={betActionButtonSx}
                >
                  決定
                </Button>
                <Button
                  disabled={!canCancelPending}
                  variant="outlined"
                  onClick={() => onCancelBet(index)}
                  sx={cancelPendingButtonSx}
                >
                  キャンセル
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>

      {disabledBySideLock && !canCancelConfirmedBet && (
        <Box sx={{ mt: 1, fontSize: '12px', color: 'rgba(0,0,0,0.62)', fontWeight: 700 }}>
          {sideLockMessage}
        </Box>
      )}
    </Box>
  );
}

export default BetChoiceCardPC;
